import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Lock, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import api from "../../services/api";

export default function MFAVerifyPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithToken } = useAuth();

  const mfaToken = location.state?.mfa_token || "";
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes for TOTP
  const [emailCodeTimeLeft, setEmailCodeTimeLeft] = useState(600); // 10 minutes for email code
  const [usingEmailCode, setUsingEmailCode] = useState(false);
  const [emailCodeSent, setEmailCodeSent] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (!mfaToken) navigate("/login", { replace: true });
  }, [mfaToken, navigate]);

  // TOTP Countdown timer
  useEffect(() => {
    if (usingEmailCode || timeLeft <= 0) return;
    const timer = setInterval(() => {
      setTimeLeft((t) => t - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [timeLeft, usingEmailCode]);

  // Email code countdown timer
  useEffect(() => {
    if (!usingEmailCode || emailCodeTimeLeft <= 0) return;
    const timer = setInterval(() => {
      setEmailCodeTimeLeft((t) => t - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [emailCodeTimeLeft, usingEmailCode]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleDigitChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    // Handle paste: if multiple digits pasted, distribute across fields
    if (value.length > 1) {
      const digits = value.slice(0, 6 - index).split("");
      const newCode = [...code];
      digits.forEach((digit, i) => {
        if (index + i < 6) {
          newCode[index + i] = digit;
        }
      });
      setCode(newCode);
      // Focus on the last filled field
      const lastFilledIndex = Math.min(index + digits.length - 1, 5);
      setTimeout(() => inputRefs.current[lastFilledIndex]?.focus(), 0);
      return;
    }

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-advance to next field
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const fullCode = code.join("");
    if (fullCode.length < 6) {
      toast.error("Please enter all 6 digits");
      return;
    }

    setLoading(true);
    try {
      const res = await api.post("/mfa/verify", {
        mfa_token: mfaToken,
        code: fullCode,
        method: usingEmailCode ? "email" : "totp",
      });
      const { access_token, user } = res.data;
      await loginWithToken(access_token, user);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid code. Please try again.");
      setCode(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      await api.post("/mfa/resend-code", { mfa_token: mfaToken });
      setTimeLeft(300);
      toast.success("Code sent to your authenticator");
    } catch (err) {
      toast.error("Failed to resend code");
    }
  };

  const handleMethodSwitch = async () => {
    setLoading(true);
    try {
      await api.post("/mfa/email-code/send", { mfa_token: mfaToken });
      setUsingEmailCode(true);
      setEmailCodeSent(true);
      setEmailCodeTimeLeft(600); // Reset 10-minute timer
      setCode(["", "", "", "", "", ""]); // Clear TOTP input
      toast.success("Code sent to your email");
      inputRefs.current[0]?.focus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send email code");
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchBack = () => {
    setUsingEmailCode(false);
    setEmailCodeSent(false);
    setCode(["", "", "", "", "", ""]);
    setTimeLeft(300); // Reset TOTP timer
    inputRefs.current[0]?.focus();
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      {/* INNER CONTAINER - Navy background with grid pattern and glows */}
      <div
        className="w-full max-w-6xl h-screen lg:h-auto lg:rounded-3xl lg:shadow-2xl lg:overflow-hidden flex flex-col lg:flex-row relative"
        style={{
          backgroundImage: `
            linear-gradient(0deg, transparent 24%, rgba(150,190,255,.55) 25%, rgba(150,190,255,.55) 26%, transparent 27%, transparent 74%, rgba(150,190,255,.55) 75%, rgba(150,190,255,.55) 76%, transparent 77%, transparent),
            linear-gradient(90deg, transparent 24%, rgba(150,190,255,.55) 25%, rgba(150,190,255,.55) 26%, transparent 27%, transparent 74%, rgba(150,190,255,.55) 75%, rgba(150,190,255,.55) 76%, transparent 77%, transparent)
          `,
          backgroundSize: "50px 50px",
          backgroundColor: "#0f1b2e",
        }}
      >
        {/* Glow overlays - hidden on mobile/tablet */}
        <div className="hidden lg:block absolute top-0 right-[30rem] w-56 h-56 bg-blue-500/30 rounded-full blur-3xl pointer-events-none z-20" />
        <div className="hidden lg:block absolute bottom-[1rem] left-[2rem] w-56 h-56 bg-blue-600/25 rounded-full blur-3xl pointer-events-none z-20" />

        {/* LEFT PANEL - 60% width on desktop */}
        <div className="hidden lg:flex lg:w-3/5 flex-col justify-between p-16 text-white relative z-10">
          {/* Dark overlay for text readability */}
          <div className="absolute inset-0 bg-[#0a1628]/95 z-0" />
          {/* Content wrapper */}
          <div className="relative z-10 flex flex-col justify-between h-full">
            {/* Logo */}
            <div className="flex items-center gap-3 mb-12">
              <img src="/assura/icononly_transparent.png" alt="Assura" className="h-16 w-16" />
              <span className="text-2xl font-bold tracking-wide">ASSURA</span>
            </div>

            {/* Hero Copy */}
            <div className="flex-1 flex flex-col justify-center">
              <h1 className="text-5xl lg:text-6xl font-bold leading-tight mb-8">
                Your account
                <br />
                <span className="text-[#378ADD]">is protected</span>
                <br />
                by MFA.
              </h1>
              <p className="text-base text-slate-400 leading-relaxed max-w-md">
                Multi-factor authentication keeps your compliance data and client records secure. Check your
                authenticator app or email.
              </p>
            </div>

            {/* Security Badges */}
            <div className="flex flex-wrap gap-3 mt-auto">
              <div className="px-4 py-2 border-2 border-[#378ADD]/50 rounded-lg text-xs font-medium text-slate-300 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#378ADD]" />
                256-bit encrypted
              </div>
              <div className="px-4 py-2 border-2 border-[#378ADD]/50 rounded-lg text-xs font-medium text-slate-300 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#378ADD]" />
                Session-bound tokens
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT PANEL - 40% width on desktop, full width on mobile - WHITE BACKGROUND */}
        <div className="flex-1 lg:w-2/5 bg-white flex items-center justify-center p-6 lg:p-12 relative z-10">
          <div className="w-full max-w-sm">
            {/* Logo for mobile */}
            <div className="lg:hidden mb-8 flex items-center gap-3 justify-center">
              <img src="/assura/icononly_transparent.png" alt="Assura" className="h-10 w-10" />
              <span className="font-bold text-gray-900 text-lg">ASSURA</span>
            </div>

            {/* Step Indicator */}
            <div className="mb-8 flex items-center justify-center gap-3">
              <div className="w-2.5 h-2.5 rounded-full bg-blue-600" />
              <div className="flex-1 max-w-xs h-0.5 bg-blue-600" />
              <div className="w-2.5 h-2.5 rounded-full bg-blue-600" />
            </div>
            <p className="text-center text-xs font-semibold text-gray-500 mb-8 uppercase tracking-wide">2 of 2</p>

            {/* Lock Icon */}
            <div className="flex justify-center mb-8">
              <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
                <Lock className="h-8 w-8 text-blue-600" />
              </div>
            </div>

            {/* Form Header */}
            <div className="mb-8 text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Two-step verification</h2>
              <p className="text-sm text-gray-600">
                {usingEmailCode ? "Enter the 6-digit code from your email" : "Enter the 6-digit code from your authenticator app"}
              </p>
            </div>

            {/* OTP Input */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="flex justify-center gap-2">
                {code.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    placeholder="−"
                    value={digit}
                    onChange={(e) => handleDigitChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    className={`w-14 h-14 text-center text-2xl font-bold rounded-xl border-2 transition-all ${
                      digit ? "bg-[#E6F1FB] border-blue-600 text-blue-900" : "bg-white border-gray-300 text-gray-400"
                    } focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent placeholder:text-gray-300`}
                  />
                ))}
              </div>

              {/* Timer and Resend */}
              <div className="flex items-center justify-between text-sm">
                <div className="text-gray-600">
                  Code expires in <span className="font-semibold">{formatTime(usingEmailCode ? emailCodeTimeLeft : timeLeft)}</span>
                </div>
                {usingEmailCode ? (
                  <button
                    type="button"
                    onClick={handleMethodSwitch}
                    disabled={emailCodeTimeLeft > 540}
                    className="text-blue-600 hover:text-blue-700 font-medium disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    Resend code
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={timeLeft > 240}
                    className="text-blue-600 hover:text-blue-700 font-medium disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    Resend code
                  </button>
                )}
              </div>

              {/* Method Fallback - Box Style */}
              <div className="flex items-center justify-between px-4 py-3 border border-gray-200 rounded-lg bg-[#F8F9FA]">
                <span className="text-sm text-gray-600 flex items-center gap-2">
                  <span>{usingEmailCode ? "🔐" : "✉️"}</span>
                  <span>{usingEmailCode ? "Back to authenticator app" : "Use email code instead"}</span>
                </span>
                <button
                  type="button"
                  onClick={usingEmailCode ? handleSwitchBack : handleMethodSwitch}
                  disabled={loading}
                  className="text-blue-600 hover:text-blue-700 font-medium text-sm disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  {usingEmailCode ? "Switch back" : "Switch"}
                </button>
              </div>

              {/* Verify Button */}
              <Button
                type="submit"
                disabled={loading || code.join("").length < 6}
                variant="primary"
                size="lg"
                className="w-full mt-8"
              >
                {loading ? "Verifying..." : "Verify and sign in"}
              </Button>
            </form>

            {/* Back Link */}
            <div className="mt-6 text-center">
              <Link
                to="/login"
                className="text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors flex items-center justify-center gap-1"
              >
                <span>‹</span>
                <span>Back to sign in</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
